export function normalizeHexColor(raw: string | null | undefined): string | null {
	if (raw == null) return null;
	let h = String(raw).trim();
	if (!h) return null;
	if (!h.startsWith('#')) h = `#${h}`;
	return /^#[0-9a-fA-F]{6}$/.test(h) ? h : null;
}

export function withHexAlpha(hex: string | null | undefined, alphaHex: string): string | null {
	const h = normalizeHexColor(hex);
	if (!h) return null;
	return `${h}${alphaHex}`;
}

/** Soft fill for benchmark/milestone legend chips (same alpha as challenge header tint). */
export function legendChipTint(hex: string | null | undefined): string {
	return withHexAlpha(hex, '33') ?? 'rgba(148, 163, 184, 0.14)';
}

export function legendChipForeground(hex: string | null | undefined): string {
	return normalizeHexColor(hex) ?? '#475569';
}

/** Phase color is DB-driven (prefer `phase.color` from API); this is a neutral UI fallback only. */
export const DEFAULT_PHASE_FALLBACK_HEX = '#64748B'; // slate-500

/** Prefer DB-driven color; fallback to a neutral gray for missing/misconfigured data. */
export function resolvePhaseColorHex(raw: string | null | undefined): string {
	return normalizeHexColor(raw) ?? DEFAULT_PHASE_FALLBACK_HEX;
}

export type ParaBadgeColors = { bg: string; fg: string; border: string };

const DEFAULT_BADGE_BG = '#F1F5F9'; // slate-100
const DEFAULT_BADGE_FG = '#334155'; // slate-700
const DEFAULT_BADGE_BORDER = '#CBD5E1'; // slate-300
const BADGE_FG_ON_TINT = '#0F172A'; // slate-900

/**
 * Domain-style badge colors used throughout PARA:
 * - bg: lightly tinted from accent hex (or slate default)
 * - fg: high contrast text on tinted bg
 * - border: solid accent hex (or slate default)
 */
export function domainBadgeColorsFromAccentHex(hex: string | null | undefined): ParaBadgeColors {
	const h = normalizeHexColor(hex);
	if (!h) {
		return { bg: DEFAULT_BADGE_BG, fg: DEFAULT_BADGE_FG, border: DEFAULT_BADGE_BORDER };
	}
	return { bg: legendChipTint(h), fg: BADGE_FG_ON_TINT, border: h };
}

/** Accepts `#RRGGBB` or `RRGGBB` (returns `#RRGGBB` or null). */
export function normalizeChipColor(raw: string | null | undefined): string | null {
	return normalizeHexColor(raw);
}

/**
 * Rough perceived brightness for `#RRGGBB` only (255 = white). Useful to reject pastel milestone
 * chips that disappear on tinted badge fills.
 */
export function paraChipBrightness255(hex: string | null | undefined): number | null {
	const h = normalizeHexColor(hex);
	if (!h) return null;
	const r = parseInt(h.slice(1, 3), 16);
	const g = parseInt(h.slice(3, 5), 16);
	const b = parseInt(h.slice(5, 7), 16);
	return Math.round((r * 299 + g * 587 + b * 114) / 1000);
}

/** True when a stroke in this color would likely wash out on light PARA badge tints. */
export function isParaHexLikelyTooLightForStroke(hex: string | null | undefined, threshold = 200): boolean {
	const y = paraChipBrightness255(hex);
	return y != null && y > threshold;
}

